import { compareAlphabetically, dedup, isNil } from '@gfe/core/utils/shared-utils';

export type UserVehiclePlacement = {
  vehicle_uid: string;
  type: string;
};

type UserTrailerPlacement = {
  trailer_uid: string;
  type: string;
};

interface UserCompartmentCopyPlacement {
  trailer_uid: string;
  compartment_uid: string;
  type: string;
  copy: number;
}

interface UserCompartmentRegistrationPlacement {
  trailer_uid: string;
  compartment_uid: string;
  type: string;
  registration: string;
}

export type UserPlacement =
  | UserCompartmentCopyPlacement
  | UserCompartmentRegistrationPlacement
  | UserTrailerPlacement
  | UserVehiclePlacement;

export function isVehiclePlacement(
  placement: UserPlacement
): placement is UserVehiclePlacement {
  return placement.type.toLowerCase() === 'vehicle';
}

/** Single source of both the `OccupantKind` type and the runtime guard against wire drift. */
const OCCUPANT_KINDS = [
  'Commodity',
  'Product',
  'CapacityResource',
  'Container',
  'Equipment',
  'Temperature'
] as const;

export type OccupantKind = (typeof OCCUPANT_KINDS)[number];

/** The kinds a move can carry: a temperature is a state a placement is in, not a delta. */
export type MovedOccupantKind = Exclude<OccupantKind, 'Temperature'>;

/**
 * The wire may carry an occupant kind this model does not know, since the backend adds kinds
 * ahead of the front end. Such a row is skipped rather than flattened: a flattened row of an
 * unmodelled kind has no quantity to read, and every route and stopover view reads them.
 */
function isKnownOccupantKind(kind: string): boolean {
  return (OCCUPANT_KINDS as readonly string[]).includes(kind);
}

interface UserOccupantCommodity {
  occupant_kind: 'Commodity';
  commodity_uid: string;
  product_uid?: string;
  quantity_axis: 'capacity' | 'count';
  capacity_resource_uid?: string;
  quantity: number;
}

interface UserOccupantProduct {
  occupant_kind: 'Product';
  product_uid: string;
  quantity_axis: 'capacity' | 'count';
  capacity_resource_uid?: string;
  quantity: number;
}

interface UserOccupantCapacityResource {
  occupant_kind: 'CapacityResource';
  capacity_resource_uid: string;
  quantity: number;
}

interface UserOccupantContainer {
  occupant_kind: 'Container';
  container_uid: string;
  quantity_axis: 'capacity' | 'count';
  capacity_resource_uid?: string;
  quantity: number;
}

interface UserOccupantEquipment {
  occupant_kind: 'Equipment';
  equipment_uid: string;
  quantity_axis: 'capacity' | 'count';
  capacity_resource_uid?: string;
  quantity: number;
}

interface UserOccupantTemperature {
  occupant_kind: 'Temperature';
  temperature_uid: string;
}

type UserMeasurableOccupant =
  | UserOccupantCommodity
  | UserOccupantProduct
  | UserOccupantCapacityResource
  | UserOccupantContainer
  | UserOccupantEquipment;

/**
 * One thing occupying a placement, tagged by `occupant_kind`. The same occupant describes both
 * views of a stopover: what a `UserMove` moved (a delta) and what a `UserHolding` holds
 * afterwards (a level).
 */
export type UserMovedOrHeld = UserMeasurableOccupant | UserOccupantTemperature;

/**
 * How a move acts on its placement: contents are loaded or unloaded, a container or piece of
 * equipment is attached or detached, and `replace` restates the placement's temperature.
 */
export type MovementKind = 'load' | 'unload' | 'attach' | 'detach' | 'replace';

export type UserMove = { kind: MovementKind } & UserMovedOrHeld;

/**
 * A temperature move restates a state rather than moving a quantity, so it has no delta to
 * flatten; every other known kind carries one.
 */
function isMeasurableMove(
  move: UserMove
): move is { kind: MovementKind } & UserMeasurableOccupant {
  return isKnownOccupantKind(move.occupant_kind) && move.occupant_kind !== 'Temperature';
}

export interface UserMovement {
  job_uid?: string;
  operation_uid?: string;
  operation_entity_type?: string;
  operation_kind?: string;
  service_start: string;
  service_end: string;
  placement?: UserPlacement;
  moved: UserMove[];
}

export interface FlatMove {
  job_uid?: string;
  operation_kind?: string;
  placement?: UserPlacement;
  kind: MovementKind;
  occupant_kind: MovedOccupantKind;
  commodity_uid?: string;
  product_uid?: string;
  container_uid?: string;
  equipment_uid?: string;
  capacity_resource_uid?: string;
  axis: 'capacity' | 'count';
  quantity: number;
}

export function isCompartmentPlacement(
  placement: UserPlacement
): placement is UserCompartmentCopyPlacement | UserCompartmentRegistrationPlacement {
  const t = placement.type.toLowerCase();
  return t === 'compartmentcopy' || t === 'compartmentregistration';
}

/**
 * Returns true if there are multiple distinct copy indices for the given compartment UID
 * among the provided placements. Used to determine whether to display the copy index.
 */
export function hasMultipleCompartmentCopies(
  placements: UserPlacement[],
  compartmentUid: string
): boolean {
  const copies = new Set<number>();
  for (const placement of placements) {
    if (placement.type.toLowerCase() === 'compartmentcopy') {
      const p = placement as UserCompartmentCopyPlacement;
      if (p.compartment_uid === compartmentUid) {
        copies.add(p.copy);
        if (copies.size > 1) return true;
      }
    }
  }
  return false;
}

export function getCompartmentPlacementKey(
  placement: UserPlacement,
  showCopyIndex: boolean = true
): string {
  if (placement.type.toLowerCase() === 'compartmentcopy') {
    const p = placement as UserCompartmentCopyPlacement;
    return showCopyIndex ? `${p.compartment_uid} #${p.copy}` : p.compartment_uid;
  }
  if (placement.type.toLowerCase() === 'compartmentregistration') {
    const p = placement as UserCompartmentRegistrationPlacement;
    return `${p.compartment_uid} (${p.registration})`;
  }
  if (isVehiclePlacement(placement)) return placement.vehicle_uid;
  return (placement as UserTrailerPlacement).trailer_uid;
}

/**
 * Display key for one placement, read in the context of its sibling placements: a compartment
 * copy index is shown only when the same compartment appears under more than one copy.
 */
export function placementKeyIn(
  placements: UserPlacement[],
  placement: UserPlacement
): string {
  const showCopyIndex =
    placement.type.toLowerCase() === 'compartmentcopy'
      ? hasMultipleCompartmentCopies(
          placements,
          (placement as UserCompartmentCopyPlacement).compartment_uid
        )
      : true;
  return getCompartmentPlacementKey(placement, showCopyIndex);
}

export function hasCompartmentPlacements(movements: UserMovement[]): boolean {
  return movements.some((m) => !isNil(m.placement) && isCompartmentPlacement(m.placement));
}

export function extractCompartmentUids(movements: UserMovement[]): string[] {
  const allPlacements = movements
    .map((m) => m.placement)
    .filter((p): p is UserPlacement => !isNil(p));
  const keys = allPlacements
    .map((placement) =>
      isCompartmentPlacement(placement) ? placementKeyIn(allPlacements, placement) : null
    )
    .filter((key) => !isNil(key)) as string[];

  return Array.from(new Set(keys));
}

/**
 * Flattens the nested movement log into one row per physical delta. The capacity-vs-count axis
 * and the resolved capacity resource are settled here so downstream aggregations never re-inspect
 * the `occupant_kind` variant shape.
 */
export function flattenMoves(movements: UserMovement[]): FlatMove[] {
  const rows: FlatMove[] = [];
  for (const movement of movements) {
    for (const move of movement.moved) {
      if (!isMeasurableMove(move)) continue;
      const axis: 'capacity' | 'count' =
        move.occupant_kind === 'CapacityResource' ? 'capacity' : move.quantity_axis;
      const capacityResourceUid =
        move.occupant_kind === 'CapacityResource'
          ? move.capacity_resource_uid
          : move.quantity_axis === 'capacity'
            ? move.capacity_resource_uid
            : undefined;
      rows.push({
        job_uid: movement.job_uid,
        operation_kind: movement.operation_kind,
        placement: movement.placement,
        kind: move.kind,
        occupant_kind: move.occupant_kind,
        commodity_uid: move.occupant_kind === 'Commodity' ? move.commodity_uid : undefined,
        product_uid:
          move.occupant_kind === 'Commodity' || move.occupant_kind === 'Product'
            ? move.product_uid
            : undefined,
        container_uid: move.occupant_kind === 'Container' ? move.container_uid : undefined,
        equipment_uid: move.occupant_kind === 'Equipment' ? move.equipment_uid : undefined,
        capacity_resource_uid: capacityResourceUid,
        axis,
        quantity: move.quantity
      });
    }
  }
  return rows;
}

export function formatCapacityValue(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(2);
}

/**
 * Converts compartment quantities map to display format
 * @param quantities Map of compartment -> { capacity_uid -> quantity }
 * @returns Map of compartment -> formatted string (e.g., "100 kg, 50 liters")
 */
export function formatCompartmentQuantities(
  quantities: Record<string, Record<string, number>>
): Record<string, string> {
  const formatted: Record<string, string> = {};

  Object.entries(quantities).forEach(([compartment, capacities]) => {
    const parts = Object.entries(capacities)
      .filter(([, quantity]) => quantity > 1e-10)
      .sort(([capacityUidA], [capacityUidB]) =>
        compareAlphabetically(capacityUidA, capacityUidB)
      )
      .map(([capacityUid, quantity]) => `${formatCapacityValue(quantity)} ${capacityUid}`);

    if (parts.length > 0) {
      formatted[compartment] = parts.join(', ');
    }
  });

  return formatted;
}

export type UserHolding = { placement: UserPlacement } & UserMovedOrHeld;

export interface FlatHolding {
  placement: UserPlacement;
  occupant_kind: OccupantKind;
  commodity_uid?: string;
  product_uid?: string;
  container_uid?: string;
  equipment_uid?: string;
  capacity_resource_uid?: string;
  temperature_uid?: string;
  /** Undefined for `Temperature`, which is a state fact rather than a quantity. */
  axis?: 'capacity' | 'count';
  quantity?: number;
}

function flattenHolding(holding: UserHolding): FlatHolding {
  const base = { placement: holding.placement, occupant_kind: holding.occupant_kind };
  switch (holding.occupant_kind) {
    case 'Temperature':
      return { ...base, temperature_uid: holding.temperature_uid };
    case 'CapacityResource':
      return {
        ...base,
        capacity_resource_uid: holding.capacity_resource_uid,
        axis: 'capacity' as const,
        quantity: holding.quantity
      };
    case 'Commodity':
      return {
        ...base,
        commodity_uid: holding.commodity_uid,
        product_uid: holding.product_uid,
        capacity_resource_uid:
          holding.quantity_axis === 'capacity' ? holding.capacity_resource_uid : undefined,
        axis: holding.quantity_axis,
        quantity: holding.quantity
      };
    case 'Product':
      return {
        ...base,
        product_uid: holding.product_uid,
        capacity_resource_uid:
          holding.quantity_axis === 'capacity' ? holding.capacity_resource_uid : undefined,
        axis: holding.quantity_axis,
        quantity: holding.quantity
      };
    case 'Container':
      return {
        ...base,
        container_uid: holding.container_uid,
        capacity_resource_uid:
          holding.quantity_axis === 'capacity' ? holding.capacity_resource_uid : undefined,
        axis: holding.quantity_axis,
        quantity: holding.quantity
      };
    case 'Equipment':
      return {
        ...base,
        equipment_uid: holding.equipment_uid,
        capacity_resource_uid:
          holding.quantity_axis === 'capacity' ? holding.capacity_resource_uid : undefined,
        axis: holding.quantity_axis,
        quantity: holding.quantity
      };
  }
}

/**
 * Flattens the holdings log into one row per held level, settling the capacity-vs-count axis and
 * the resolved capacity resource so downstream aggregations never re-inspect the `occupant_kind`
 * variant shape. Mirrors `flattenMoves`.
 */
export function flattenHoldings(holdings: UserHolding[]): FlatHolding[] {
  return holdings
    .filter((holding) => isKnownOccupantKind(holding.occupant_kind))
    .map(flattenHolding);
}

/**
 * Which occupant a held row belongs to. `CapacityResource` rows have no occupant: the backfill of
 * pre-holdings data produced exactly that shape, since the old wire never carried occupant
 * identity.
 */
export function occupantUidOf(row: FlatHolding): string | undefined {
  if (row.occupant_kind === 'Commodity') return row.commodity_uid;
  if (row.occupant_kind === 'Product') return row.product_uid;
  if (row.occupant_kind === 'Container') return row.container_uid;
  if (row.occupant_kind === 'Equipment') return row.equipment_uid;
  return undefined;
}

const QUANTITY_EPSILON = 1e-10;

/** Separates fields inside composite grouping keys; no uid can contain NUL. */
const GROUP_KEY_SEPARATOR = '\u0000';

/** Distinct temperatures held after an action, in first-seen order. */
export function heldTemperatureUids(holdings: UserHolding[]): string[] {
  return dedup(
    holdings.flatMap((holding) =>
      holding.occupant_kind === 'Temperature' ? [holding.temperature_uid] : []
    )
  );
}

export interface TemperatureSegment {
  temperatureUid: string;
  percentage: number;
  /** The placements behind this segment carry nothing, so the timeline strokes it dashed. */
  dashed: boolean;
}

/**
 * Share of placements held at each temperature after an action, for the timeline's segmented
 * display. Placements sharing a temperature collapse into one segment only when they also agree
 * on whether they are carrying anything, so an emptied compartment gets its own dashed line
 * while its still-loaded neighbour at the same temperature keeps a solid one.
 *
 * `cargo` is what is actually aboard on the leg being drawn, which is not the temperature state:
 * the wire stops reporting a temperature holding once its placement is emptied, so emptiness has
 * to be read from the cargo holdings rather than from the temperatures' own absence. Omitting it
 * marks nothing dashed, for views whose legs always carry their load by construction.
 */
export function computeTemperatureSegments(
  holdings: UserHolding[],
  cargo?: UserHolding[]
): TemperatureSegment[] | undefined {
  const loaded = isNil(cargo) ? undefined : loadedPlacements(cargo);
  const groups = new Map<string, TemperatureSegment>();
  let totalCount = 0;

  holdings.forEach((holding) => {
    if (holding.occupant_kind !== 'Temperature') return;
    const dashed = isNil(loaded)
      ? false
      : !loaded.has(placementIdentity(holding.placement));
    const key = `${holding.temperature_uid}${GROUP_KEY_SEPARATOR}${dashed}`;
    const group = groups.get(key);
    if (isNil(group)) {
      groups.set(key, {
        temperatureUid: holding.temperature_uid,
        percentage: 1,
        dashed
      });
    } else {
      group.percentage += 1;
    }
    totalCount++;
  });

  if (totalCount === 0) return undefined;

  return [...groups.values()].map((group) => ({
    ...group,
    percentage: (100.0 * group.percentage) / totalCount
  }));
}

/**
 * Identity of a placement, as opposed to `placementKeyIn`'s display name: two copies of one
 * compartment are distinct placements even where the display would collapse them.
 */
function placementIdentity(placement: UserPlacement): string {
  const trailer = 'trailer_uid' in placement ? placement.trailer_uid : '';
  return [
    placement.type.toLowerCase(),
    getCompartmentPlacementKey(placement),
    trailer
  ].join(' ');
}

/**
 * Identities of the placements carrying something. A `Temperature` row states a placement's
 * setting rather than its load, so it never makes a placement count as loaded.
 */
function loadedPlacements(holdings: UserHolding[]): Set<string> {
  return new Set(
    flattenHoldings(holdings)
      .filter((row) => !isNil(row.quantity) && row.quantity > QUANTITY_EPSILON)
      .map((row) => placementIdentity(row.placement))
  );
}

/**
 * Advances the temperature each placement is in by one action, mirroring the data layer's own
 * fold: a `Replace` supersedes that placement's temperature and stands until the next one, so
 * a placement this action does not name keeps the temperature it had.
 *
 * Holdings are folded in too, because they are the only place a solution optimized before
 * transitions were logged reports a temperature at all. Both sources upsert rather than replace
 * the state, since the data layer reports a temperature holding only while its placement still
 * holds cargo — a placement that has been emptied is silent, not unrefrigerated.
 *
 * The result is itself a list of `Temperature` holdings, so `heldTemperatureUids` and
 * `computeTemperatureSegments` read it exactly as they read an action's own holdings.
 */
export function foldTemperatureState(
  current: UserHolding[],
  movements: UserMovement[],
  holdings: UserHolding[]
): UserHolding[] {
  const stated = new Map(current.map((held) => [placementIdentity(held.placement), held]));

  for (const movement of movements) {
    const placement = movement.placement;
    if (isNil(placement)) continue;
    for (const move of movement.moved) {
      if (move.kind !== 'replace' || move.occupant_kind !== 'Temperature') continue;
      stated.set(placementIdentity(placement), {
        placement,
        occupant_kind: 'Temperature',
        temperature_uid: move.temperature_uid
      });
    }
  }

  for (const held of holdings) {
    if (held.occupant_kind !== 'Temperature') continue;
    stated.set(placementIdentity(held.placement), held);
  }

  return [...stated.values()];
}

/**
 * The two placement questions, deliberately scoped apart: naming reads every placement, the
 * suffix decision reads only the placements being rendered.
 */
interface PlacementNaming {
  /**
   * Display key for one placement, read against every placement of the action —
   * Temperature-only ones included, since a compartment named twice is ambiguous
   * however its copies are occupied.
   */
  keyOf(placement: UserPlacement): string;
  /**
   * Whether to print the `(<placement>)` suffix, read against the placements actually
   * rendered, so a compartment held elsewhere cannot parenthesise a bare trailer uid.
   */
  needsSuffix(renderedPlacements: UserPlacement[]): boolean;
}

function placementNaming(holdings: UserHolding[]): PlacementNaming {
  const everyPlacement = holdings.map((holding) => holding.placement);
  return {
    keyOf: (placement) => placementKeyIn(everyPlacement, placement),
    needsSuffix: (renderedPlacements) => renderedPlacements.some(isCompartmentPlacement)
  };
}

interface OccupantTotals {
  occupantKind: OccupantKind;
  uid: string;
  count: number;
  capacities: Record<string, number>;
}

interface PlacementBucket {
  key: string;
  placement: UserPlacement;
  capacityTotals: Record<string, number>;
  countTotal: number;
  occupants: Map<string, OccupantTotals>;
}

/**
 * Grouping identity of an occupant: two entity types may carry the same uid, so the kind
 * belongs in the key even though only the uid is displayed.
 */
function occupantGroupKey(row: FlatHolding): string | undefined {
  const uid = occupantUidOf(row);
  return isNil(uid) ? undefined : `${row.occupant_kind}${GROUP_KEY_SEPARATOR}${uid}`;
}

function bucketHoldingsByPlacement(
  holdings: UserHolding[],
  naming: PlacementNaming
): PlacementBucket[] {
  const rows = flattenHoldings(holdings).filter(
    (row) => !isNil(row.quantity) && row.quantity > QUANTITY_EPSILON
  );
  const buckets = new Map<string, PlacementBucket>();

  for (const row of rows) {
    const key = naming.keyOf(row.placement);
    let bucket = buckets.get(key);
    if (isNil(bucket)) {
      bucket = {
        key,
        placement: row.placement,
        capacityTotals: {},
        countTotal: 0,
        occupants: new Map<string, OccupantTotals>()
      };
      buckets.set(key, bucket);
    }
    const quantity = row.quantity as number;

    if (row.axis === 'count') {
      bucket.countTotal += quantity;
    } else if (!isNil(row.capacity_resource_uid)) {
      bucket.capacityTotals[row.capacity_resource_uid] =
        (bucket.capacityTotals[row.capacity_resource_uid] ?? 0) + quantity;
    }

    const groupKey = occupantGroupKey(row);
    if (isNil(groupKey)) continue;
    let occupant = bucket.occupants.get(groupKey);
    if (isNil(occupant)) {
      occupant = {
        occupantKind: row.occupant_kind,
        uid: occupantUidOf(row) as string,
        count: 0,
        capacities: {}
      };
      bucket.occupants.set(groupKey, occupant);
    }
    if (row.axis === 'count') {
      occupant.count += quantity;
    } else if (!isNil(row.capacity_resource_uid)) {
      occupant.capacities[row.capacity_resource_uid] =
        (occupant.capacities[row.capacity_resource_uid] ?? 0) + quantity;
    }
  }

  return Array.from(buckets.values());
}

function formatCapacityParts(capacities: Record<string, number>): string[] {
  return Object.entries(capacities)
    .sort(([capUidA], [capUidB]) => compareAlphabetically(capUidA, capUidB))
    .map(([capUid, quantity]) => `${formatCapacityValue(quantity)} ${capUid}`);
}

/** Two occupants can display the same uid, so the kind breaks the tie deterministically. */
function compareOccupants(a: OccupantTotals, b: OccupantTotals): number {
  const byUid = compareAlphabetically(a.uid, b.uid);
  return byUid !== 0 ? byUid : compareAlphabetically(a.occupantKind, b.occupantKind);
}

function formatOccupantBreakdown(occupants: Map<string, OccupantTotals>): string {
  return Array.from(occupants.values())
    .sort(compareOccupants)
    .map((occupant) => {
      const parts: string[] = [];
      if (occupant.count > QUANTITY_EPSILON) {
        parts.push(`×${formatCapacityValue(occupant.count)}`);
      }
      parts.push(...formatCapacityParts(occupant.capacities));
      return parts.length > 0 ? `${occupant.uid} ${parts.join(', ')}` : '';
    })
    .filter((group) => group !== '')
    .join('; ');
}

interface PlacementAggregate {
  key: string;
  placement: UserPlacement;
  aggregate: string;
  breakdown: string;
}

function aggregatePlacements(
  holdings: UserHolding[],
  naming: PlacementNaming
): PlacementAggregate[] {
  return bucketHoldingsByPlacement(holdings, naming).flatMap((bucket) => {
    const aggregateParts = formatCapacityParts(bucket.capacityTotals);
    if (bucket.countTotal > QUANTITY_EPSILON) {
      aggregateParts.push(`×${formatCapacityValue(bucket.countTotal)}`);
    }
    if (aggregateParts.length === 0) return [];

    return [
      {
        key: bucket.key,
        placement: bucket.placement,
        aggregate: aggregateParts.join(', '),
        breakdown: formatOccupantBreakdown(bucket.occupants)
      }
    ];
  });
}

/**
 * Per placement, the totals it holds and the per-occupant breakdown behind them. Returned as two
 * parts because the placement key is displayed between them. Holdings are disjoint per
 * `(placement, occupant, axis)`, so a capacity total is a sum; the breakdown regroups those rows
 * by occupant so one commodity never renders on two lines.
 */
export function aggregateHoldingsByPlacement(
  holdings: UserHolding[]
): Record<string, { aggregate: string; breakdown: string }> {
  const entries = aggregatePlacements(holdings, placementNaming(holdings));
  const result: Record<string, { aggregate: string; breakdown: string }> = {};
  entries.forEach(({ key, aggregate, breakdown }) => {
    result[key] = { aggregate, breakdown };
  });
  return result;
}

/**
 * One display value per placement: `<totals> (<placement>) — <breakdown>`. The em dash
 * appears only when occupant identity is available — backfilled holdings carry none.
 */
export function composeHoldingLabelValues(holdings: UserHolding[]): string[] {
  const naming = placementNaming(holdings);
  const entries = aggregatePlacements(holdings, naming);
  const showPlacement = naming.needsSuffix(entries.map((entry) => entry.placement));

  return entries.map(({ key, aggregate, breakdown }) => {
    const head = showPlacement ? `${aggregate} (${key})` : aggregate;
    return breakdown === '' ? head : `${head} — ${breakdown}`;
  });
}

interface CountGroup {
  occupantKind: OccupantKind;
  uid: string;
  placementKey: string;
  quantity: number;
}

function compareCountGroups(a: CountGroup, b: CountGroup): number {
  const byUid = compareAlphabetically(a.uid, b.uid);
  if (byUid !== 0) return byUid;
  const byPlacement = compareAlphabetically(a.placementKey, b.placementKey);
  return byPlacement !== 0
    ? byPlacement
    : compareAlphabetically(a.occupantKind, b.occupantKind);
}

/**
 * Count-axis levels held, keyed by occupant and qualified by placement when a rendered
 * placement is a compartment. `CapacityResource` holdings carry no occupant identity, so
 * an action holding only those reads empty here.
 */
export function countsByOccupantAndPlacement(
  holdings: UserHolding[]
): Record<string, string> {
  const naming = placementNaming(holdings);
  const rows = flattenHoldings(holdings).filter(
    (row) =>
      row.axis === 'count' &&
      !isNil(row.quantity) &&
      row.quantity > QUANTITY_EPSILON &&
      !isNil(occupantUidOf(row))
  );
  const showPlacement = naming.needsSuffix(rows.map((row) => row.placement));

  const groups = new Map<string, CountGroup>();
  for (const row of rows) {
    const placementKey = naming.keyOf(row.placement);
    const occupantKey = occupantGroupKey(row) as string;
    // Placements only separate groups when the placement is displayed, otherwise one
    // occupant held at several trailers reads as one total.
    const groupKey = showPlacement
      ? `${occupantKey}${GROUP_KEY_SEPARATOR}${placementKey}`
      : occupantKey;
    const group = groups.get(groupKey);
    if (isNil(group)) {
      groups.set(groupKey, {
        occupantKind: row.occupant_kind,
        uid: occupantUidOf(row) as string,
        placementKey,
        quantity: row.quantity as number
      });
    } else {
      group.quantity += row.quantity as number;
    }
  }

  const ordered = Array.from(groups.values()).sort(compareCountGroups);
  const displayKeyOf = (group: CountGroup, occupant: string): string =>
    showPlacement ? `${occupant}: ${group.placementKey}` : occupant;

  const bareKeyUses = new Map<string, number>();
  ordered.forEach((group) => {
    const bare = displayKeyOf(group, group.uid);
    bareKeyUses.set(bare, (bareKeyUses.get(bare) ?? 0) + 1);
  });

  const formatted: Record<string, string> = {};
  for (const group of ordered) {
    const bare = displayKeyOf(group, group.uid);
    // A map key cannot repeat, so the occupant kind enters the displayed occupant only when
    // it is the one thing telling two entries apart; a uid of one kind stays bare.
    const key =
      (bareKeyUses.get(bare) ?? 0) > 1
        ? displayKeyOf(group, `${group.uid} (${group.occupantKind})`)
        : bare;
    formatted[key] = formatCapacityValue(group.quantity);
  }
  return formatted;
}
